import { useEffect, useState } from "react";
import { PauseCircle, Send, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { PageTitle, Loader, EmptyState } from "../components/kit";

const PRESETS = [
  "Reply to every open complaint with your side of the story and any evidence",
  "Contact affected customers to offer a fix, return visit or refund where appropriate",
  "Confirm your public liability insurance is valid and upload the current certificate",
  "Review your services, prices and service area so your profile is accurate",
  "Confirm you'll send a written quote with scope, price and start date before every job",
  "Confirm you'll give at least 24 hours' notice if you need to cancel or rearrange",
  "Read the Service Provider Terms & Conditions and the Acceptable Use Policy",
];

function PlanStatus({ plan }) {
  if (!plan) return <span className="chip bg-slate-100 text-slate-600">No plan sent</span>;
  if (plan.status === "submitted") return <span className="chip bg-emerald-100 text-emerald-800" data-testid="plan-status-ready">Ready for review</span>;
  return <span className="chip bg-amber-100 text-amber-800" data-testid="plan-status-progress">In progress {plan.done}/{plan.total}</span>;
}

function SendPlan({ row, onDone }) {
  const [picked, setPicked] = useState(PRESETS.slice(0, 3));
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const toggle = (t) => setPicked((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const send = async () => {
    const items = [...picked, ...custom.split("\n").map((s) => s.trim()).filter(Boolean)];
    try { await api.post(`/admin/providers/${row.id}/improvement-plan`, { items, note }); toast.success("Improvement plan sent"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-3 text-sm" data-testid="send-plan-form">
      {row.plan && <p className="text-xs text-amber-700">Sending a new plan replaces the current one.</p>}
      <div className="space-y-2">{PRESETS.map((t, i) => <label key={t} className="flex gap-2 items-start"><input type="checkbox" className="mt-1" checked={picked.includes(t)} onChange={() => toggle(t)} data-testid={`plan-preset-${i}`} />{t}</label>)}</div>
      <textarea className="input" rows={2} placeholder="Add your own steps, one per line (optional)" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="plan-custom-items" />
      <textarea className="input" rows={2} placeholder="Message to the professional (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="plan-note" />
      <button onClick={send} className="btn-coral btn-sm" data-testid="plan-send-btn"><Send size={14} />Send improvement plan</button>
    </div>
  );
}

function PlanItems({ plan }) {
  return (
    <ul className="space-y-2 text-sm" data-testid="plan-items">{plan.items.map((i) => (
      <li key={i.id} className="flex gap-2">{i.done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" /> : <Circle size={16} className="text-slate-300 shrink-0 mt-0.5" />}
        <div><p className={i.done ? "" : "muted"}>{i.text}</p>{i.note && <p className="text-xs bg-white border rounded p-2 mt-1 whitespace-pre-line">{i.note}<span className="block muted mt-1">{fmtDateTime(i.done_at)}</span></p>}</div></li>))}
    </ul>
  );
}

function PausedRow({ r, reload }) {
  const [open, setOpen] = useState(null);
  const resume = async () => {
    const go = async (force) => { await api.post(`/admin/providers/${r.id}/matches`, { paused: false, force }); toast.success("New job matches resumed"); reload(); };
    try { await go(false); }
    catch (e) { if (e.response?.status === 400 && window.confirm(errMsg(e))) { try { await go(true); } catch (e2) { toast.error(errMsg(e2)); } } else if (e.response?.status !== 400) toast.error(errMsg(e)); }
  };
  return (
    <div className="card p-5 space-y-3" data-testid="paused-pro-row">
      <div className="flex flex-wrap gap-3 justify-between items-start">
        <div><b>{r.business_name}</b><p className="text-xs muted">{r.email}</p></div>
        <PlanStatus plan={r.plan} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><span className="text-xs muted block">Paused for</span><b data-testid="paused-days">{r.days_paused} day{r.days_paused === 1 ? "" : "s"}</b></div>
        <div><span className="text-xs muted block">Since</span>{fmtDate(r.paused_at)}{r.paused_by && <span className="muted"> · {r.paused_by}</span>}</div>
        <div><span className="text-xs muted block">Complaints (90 days)</span>{r.complaints_90}</div>
        <div><span className="text-xs muted block">Open complaints</span>{r.open_complaints}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setOpen(open === "send" ? null : "send")} className="btn-ghost btn-sm" data-testid="paused-send-plan-btn">{r.plan ? "Send a new plan" : "Send improvement plan"}</button>
        {r.plan && <button onClick={() => setOpen(open === "view" ? null : "view")} className="btn-ghost btn-sm" data-testid="paused-view-plan-btn">View plan ({r.plan.done}/{r.plan.total})</button>}
        <button onClick={resume} className="btn-coral btn-sm" data-testid="paused-resume-btn">Resume new job matches</button>
      </div>
      {open === "send" && <SendPlan row={r} onDone={() => { setOpen(null); reload(); }} />}
      {open === "view" && r.plan && <div className="bg-slate-50 rounded-lg p-4"><p className="text-xs muted mb-2">Sent {fmtDate(r.plan.sent_at)} by {r.plan.sent_by}{r.plan.note && ` · "${r.plan.note}"`}</p><PlanItems plan={r.plan} /></div>}
    </div>
  );
}

export default function AdminPausedPros() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/paused-providers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loader />;
  return (
    <div data-testid="admin-paused-pros">
      <PageTitle title="Paused Professionals" subtitle="Professionals whose new job matches are paused, longest paused first." />
      {!rows.length ? <EmptyState icon={PauseCircle} title="No paused professionals" text="Pause a professional's new job matches from the complaint page when there's a repeat problem." />
        : <div className="space-y-4">{rows.map((r) => <PausedRow key={r.id} r={r} reload={load} />)}</div>}
    </div>
  );
}

export { PlanItems };
